import {
  Stack,
  Box,
  Typography,
  Card,
  Button,
  useTheme,
} from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import PhoneIcon from "@mui/icons-material/Phone"
import TaskAltIcon from "@mui/icons-material/TaskAlt"
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff"
import EventBusyIcon from "@mui/icons-material/EventBusy"
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined"
import dayjs from "dayjs"
import "dayjs/locale/hu"
import { useTenant } from "../../contexts/TenantContext"
import { gradientTextSx, tint } from "../../utils/textStyles"
import { isDarkTheme, isNeonTheme } from "../../utils/themeTraits"
import CustomButton from "../../components/CustomButton"
import DiscountedPrice from "../booking/components/DiscountedPrice"
import { findProvider } from "../provider/providerChoice"
import ProviderTag from "../provider/components/ProviderTag"

// A closed booking is coloured outside the theme palette, which has nothing pale enough. Each
// carries the same light / main / dark triple a palette entry has, since the card picks the light
// end on dark cards and the dark end on paper.
const NEUTRAL = { light: "#cfd3d6", main: "#9aa0a6", dark: "#5f6368" }
const COMPLETED_COLOR = { light: "#c5d6ca", main: "#8fa897", dark: "#5f7a67" }
const EXPIRED_COLOR = { light: "#d3cbe0", main: "#a094b8", dark: "#6f6388" }

// `message` is a string, or a function of the tenant where the sentence names it.
const STATUS = {
  BOOKED: {
    label: "Jóváhagyva",
    palette: "success",
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
    message: "Ez a foglalás jóváhagyásra került. Várunk szeretettel!",
  },
  REJECTED: {
    label: "Elutasítva",
    palette: "error",
    icon: <CancelIcon sx={{ fontSize: 16 }} />,
    message: "Ez a foglalás elutasításra került.",
  },
  PENDING: {
    label: "Jóváhagyásra vár",
    palette: "warning",
    icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
    message: "Ez a foglalás még jóváhagyásra vár, emailben értesítünk amint jóváhagyták.",
  },
  // "Teljesült" says only that the time has passed uncancelled: whether the guest came is not known.
  COMPLETED: {
    label: "Teljesült",
    color: COMPLETED_COLOR,
    icon: <TaskAltIcon sx={{ fontSize: 16 }} />,
    message: "Ez az időpont már elmúlt.",
  },
  CANCELLED: {
    label: "Lemondtad",
    color: NEUTRAL,
    icon: <EventBusyIcon sx={{ fontSize: 16 }} />,
    message: "Ezt a foglalást lemondtad.",
  },
  // Without an article, so no a/az has to be chosen for the name.
  DELETED: {
    label: "Törölve",
    color: NEUTRAL,
    icon: <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />,
    message: (tenant) => (tenant?.name ? `Törölte: ${tenant.name}.` : "Ezt a foglalást törölték."),
  },
  EXPIRED: {
    label: "Lejárt",
    color: EXPIRED_COLOR,
    icon: <HistoryToggleOffIcon sx={{ fontSize: 16 }} />,
    message: "Erről a foglalásról az időpontig nem született döntés.",
  },
}

// A status this card does not know reads as nothing in particular, rather than as a promise.
const UNKNOWN_STATUS = { label: "Ismeretlen", color: NEUTRAL, icon: null, message: "" }

const statusOf = (booking) => STATUS[booking?.status] || UNKNOWN_STATUS

// Only a booking still ahead can be cancelled; everything else is history.
const CANCELLABLE_STATUSES = ["BOOKED", "PENDING"]

/**
 * Within the tenant's deadline before the start a guest can no longer cancel a BOOKED booking;
 * a PENDING one is only a request and can always be withdrawn (DECISIONS.md D28). Worked out
 * when the card renders, on the guest's clock: the backend is what enforces it, and a page left
 * open past the deadline hears it from there (DEC-0024).
 */
const isPastCancellationDeadline = (booking, deadlineHours) => {
  if (deadlineHours == null || booking?.status !== "BOOKED") return false
  const start = dayjs(`${booking.date}T${booking.startTime}`)
  return dayjs().isAfter(start.subtract(deadlineHours, "hour"))
}

/** "+36 30 123 4567" as a tel: link can dial it: digits and the leading "+" only. */
const dialable = (phoneNumber) => phoneNumber.replace(/[^\d+]/g, "")

/**
 * One booking. The date is lifted out of the sentence into a block of its own on the left —
 * the day number is the thing the eye looks for in a list — and the card is banded down
 * that edge in the colour of its status, so a rejected booking is recognisable before a
 * word of it is read.
 */
const BookingItem = ({ booking, onDelete, loading }) => {
  const theme = useTheme()
  const { tenant, displayConfig, cancellationDeadlineHours, providers, hasSeveralProviders } = useTenant()
  dayjs.locale("hu")

  const status = statusOf(booking)
  const statusColor = status.color ?? theme.palette[status.palette]
  const statusMessage = typeof status.message === "function" ? status.message(tenant) : status.message
  const isDark = isDarkTheme(displayConfig)
  // The card is dark on a dark theme and paper everywhere else, so a status colour that
  // reads on one is unreadable on the other: the light end for dark cards, the dark end
  // for paper.
  const statusInk = isDark ? statusColor.light : statusColor.dark
  // A glow in the status colour is the neon theme's; paper and the plain dark theme lift the
  // card on the same neutral shadow it rests on.
  const hoverShadowColor = isNeonTheme(displayConfig) ? tint(statusColor.main, 0.75) : tint("#000", 0.8)
  const textColor = displayConfig?.color
  // The date block is the tenant's primary colour, not its action colour: the day is a
  // label, and the only thing on the card that should read as an action is the button.
  const accent = displayConfig?.primaryColor
  const date = dayjs(booking?.date)

  const isPastDeadline = isPastCancellationDeadline(booking, cancellationDeadlineHours)
  const isCancellable = CANCELLABLE_STATUSES.includes(booking?.status) && !isPastDeadline
  const phoneNumber = displayConfig?.phoneNumber
  // Whom the guest is going to, on a tenant where that is a question (docs/MULTI_PROVIDER.md).
  const provider = hasSeveralProviders ? findProvider(providers, booking?.providerId) : undefined

  return (
    <Card
      elevation={0}
      data-testid="profile-booking"
      data-booking-id={booking?.id}
      sx={{
        width: "100%",
        borderRadius: 3,
        overflow: "hidden",
        color: textColor,
        backgroundColor: displayConfig?.sectionBackground,
        border: `1px solid ${tint(statusColor.main, isDark ? 0.35 : 0.25)}`,
        borderLeft: `5px solid ${statusColor.main}`,
        boxShadow: `0 14px 34px -24px ${tint("#000", 0.8)}`,
        transition: "transform 250ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 250ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 20px 44px -24px ${hoverShadowColor}`,
        },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:hover": { transform: "none" },
        },
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} alignItems="stretch">
        {/* Date block */}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            px: 2,
            py: { xs: 1.5, sm: 2 },
            minWidth: { sm: 108 },
            backgroundColor: tint(accent, isDark ? 0.12 : 0.14),
            borderRight: { sm: `1px solid ${tint(accent, 0.28)}` },
            borderBottom: { xs: `1px solid ${tint(accent, 0.28)}`, sm: "none" },
          }}
        >
          <Typography fontSize={34} fontWeight={800} lineHeight={1}
            sx={gradientTextSx(displayConfig, accent)}>
            {date.format("DD")}
          </Typography>
          <Typography fontSize={13} fontWeight={700} textTransform="uppercase" letterSpacing={1}
            sx={{ color: tint(textColor, 0.75) }}>
            {date.format("MMM")}
          </Typography>
          <Typography fontSize={11} textTransform="capitalize"
            sx={{ color: tint(textColor, 0.55) }}>
            {date.format("dddd")}
          </Typography>
        </Stack>

        {/* Body */}
        {/* Gaps, not margins: the deadline strip at the foot reaches the card's edges with
            negative margins, which a margin-spaced Stack would reset. */}
        <Stack spacing={1.5} useFlexGap flex={1} minWidth={0} sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Typography variant="h6" fontWeight={700} sx={{ color: textColor, minWidth: 0 }}>
              {booking?.supply?.name}
            </Typography>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                flexShrink: 0,
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                color: statusInk,
                backgroundColor: tint(statusColor.main, 0.14),
                border: `1px solid ${tint(statusColor.main, 0.4)}`,
              }}
            >
              {status.icon}
              <Typography fontSize={12} fontWeight={700} whiteSpace="nowrap">
                {status.label}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 18, color: displayConfig?.primaryColor }} />
            <Typography fontSize={15} fontWeight={600} sx={{ color: textColor }}>
              {`${booking?.startTime} – ${booking?.endTime}`}
            </Typography>
            <Typography fontSize={13} sx={{ color: tint(textColor, 0.55) }}>
              {booking?.date?.replaceAll("-", ".")}
            </Typography>
          </Stack>

          {provider && (
            <ProviderTag provider={provider} name={provider.name} testId="profile-booking-provider" />
          )}

          {/* A booking made with a coupon keeps its price: what the guest pays, and what it was. */}
          {booking?.couponCode && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap
              data-testid="profile-booking-coupon">
              <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: displayConfig?.primaryColor }} />
              <Typography fontSize={14} sx={{ color: textColor }}>
                <DiscountedPrice price={booking.price} originalPrice={booking.originalPrice} />
              </Typography>
              <Typography fontSize={13} sx={{ color: tint(textColor, 0.55) }}>
                {`kupon: ${booking.couponCode}`}
              </Typography>
            </Stack>
          )}

          {statusMessage && (
            <Typography fontSize={13} sx={{ color: tint(textColor, 0.65) }}>
              {statusMessage}
            </Typography>
          )}

          {/* In place of the cancel button, a strip across the foot of the card: why it is gone,
              and the number to call instead (DECISIONS.md D28, D31). The number stays readable,
              since a desktop browser cannot dial it. */}
          {isPastDeadline && (
            <Stack
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              columnGap={1.5}
              rowGap={1}
              sx={{
                mx: { xs: -2, sm: -2.5 },
                mb: -2,
                px: { xs: 2, sm: 2.5 },
                py: 1.25,
                backgroundColor: tint(accent, 0.08),
                borderTop: `1px solid ${tint(accent, 0.25)}`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flex="1 1 220px" minWidth={0}>
                <LockOutlinedIcon sx={{ fontSize: 18, color: accent }} />
                <Typography fontSize={13} sx={{ color: textColor }}>
                  {`Lemondás lezárva: ${cancellationDeadlineHours} órán belül kezdődik.`}
                </Typography>
              </Stack>
              {phoneNumber && (
                <Button
                  variant="outlined"
                  size="small"
                  href={`tel:${dialable(phoneNumber)}`}
                  aria-label={`Hívj minket: ${phoneNumber}`}
                  startIcon={<PhoneIcon />}
                  sx={{
                    color: accent,
                    borderColor: tint(accent, 0.55),
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { borderColor: accent, backgroundColor: tint(accent, 0.08) },
                  }}
                >
                  {phoneNumber}
                </Button>
              )}
            </Stack>
          )}

          {isCancellable && (
            <Box alignSelf={{ xs: "stretch", sm: "flex-end" }}>
              <CustomButton
                variant="outlined"
                text="Foglalás lemondása"
                textTransform="none"
                textColor={theme.palette.error.main}
                icon={<DeleteOutlineIcon fontSize="small" />}
                isLoading={loading}
                onClick={() => onDelete(booking.id)}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}

export default BookingItem
